import React, { useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTheme, useMediaQuery } from "@mui/material";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

// am5.addLicense(import.meta.env.VITE_AMCHARTS_LICENSE_KEY);

const CommonAreaChart = ({ id = "areaChartDiv", chartData, tooltipText }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const chartRef = useRef(null);
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    // ✅ Dispose existing root if exists on this DOM node (StrictMode fix)
    am5.registry.rootElements.forEach((root) => {
      if (root.dom === chartRef.current) {
        root.dispose();
      }
    });

    let root = am5.Root.new(chartRef?.current);
    rootRef.current = root;

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: root.verticalLayout,
      })
    );

    chart.get("background")?.set("fill", am5.color("#1C2032"));
    chart.get("background")?.set("fillOpacity", 1);

    // Responsive padding: smaller for xs, default for sm/md
    const chartPadding = isXs
      ? { top: 8, right: 10, bottom: 8, left: 8 }
      : { top: 10, right: 15, bottom: 10, left: 10 };
    chart.set("padding", chartPadding);

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: isXs ? 30 : 50, // Closer grid lines for xs
        }),
        min: 0,
      })
    );

    yAxis.get("renderer").grid.template.setAll({
      stroke: am5.color("#6B7280"),
      strokeWidth: 1,
      strokeOpacity: 1,
      strokeDasharray: [5, 5],
    });

    yAxis.get("renderer").labels.template.set("visible", false);

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: "day", count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: isXs ? 30 : 50, // Closer grid lines for xs
        }),
      })
    );

    xAxis.get("renderer").grid.template.setAll({
      stroke: am5.color("#E5E7EB"),
      strokeWidth: 0,
      strokeOpacity: 0,
    });

    xAxis.get("renderer").labels.template.set("visible", false);

    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "NAV",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "value",
        valueXField: "date",
        stroke: am5.color("#2B72FB"),
        fill: am5.color("#134889"),
      })
    );

    series.strokes.template.setAll({
      strokeWidth: isXs ? 1.5 : 2, // Thinner stroke for xs
      tension: 0.8,
    });

    series.fills.template.setAll({
      fill: 1,
      fillOpacity: 0.8,
      visible: true,
    });

    if (chartData?.length > 0) {
      const transformedData = chartData.map((item) => ({
        date:
          item.date instanceof Date
            ? item.date.getTime()
            : typeof item.date === "string"
              ? new Date(item.date).getTime()
              : item.date,
        value: parseFloat(item.value) || 0,
      }));

      series.data.setAll(transformedData);

      // Calculate min and max dates from data to prevent showing dates before data starts
      const dates = transformedData.map((item) => item.date).filter((d) => d != null);
      if (dates.length > 0) {
        const minDate = Math.min(...dates);
        const maxDate = Math.max(...dates);
        // Set min/max on date axis to restrict zoom/pan to actual data range
        xAxis.set("min", minDate);
        xAxis.set("max", maxDate);
        xAxis.set("strictMinMax", true);
      }

      const maxValue = Math.max(...transformedData.map((item) => item.value));
      if (maxValue > 0) {
        const interval = Math.ceil(maxValue / 9);
        yAxis.set("max", Math.ceil(maxValue / interval) * interval);
      }
    }



    series.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText:
          tooltipText ||
          "{valueX.formatDate('dd/MM/yyyy')}\nNAV: {valueY.formatNumber('#.00')}",
      })
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        xAxis: null,
        yAxis: yAxis,
        snapToSeries: [series],
      })
    );

    cursor.lineY.setAll({
      stroke: am5.color("#2B72FB"),
      strokeWidth: 2,
      strokeDasharray: [5, 5],
      visible: true,
    });

    cursor.lineX.setAll({
      visible: false,
    });

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
    };
  }, [chartData, isXs]);

  return (
    <div
      ref={chartRef}
      id={id}
      style={{
        width: "100%",
        height: isXs ? "250px" : "400px", // Smaller height for xs, default for sm/md
        backgroundColor: "#1C2032",
      }}
    />
  );
};

CommonAreaChart.propTypes = {
  id: PropTypes.string,
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.number,
        PropTypes.string,
      ]).isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
  tooltipText: PropTypes.string,
};

CommonAreaChart.defaultProps = {
  id: "areaChartDiv",
  chartData: [],
};

export default CommonAreaChart;
